const EXPERIMENT_CLASSES = {
  cartNoSurprises: 'icse-exp-cart-no-surprises',
  decisionConfidenceCard: 'icse-exp-decision-confidence-card',
};

const LOCAL_PREVIEW = {
  hostnames: new Set(['127.0.0.1', 'localhost', '[::1]']),
  overrideParam: 'icse-experiment',
};

const CART_CONFIDENCE = {
  scopeSelector: '.collection-cart-section, .cart-drawer__summary',
  eligibilitySelector: [
    '.collection-cart-section .cart-confidence-summary__treatment',
    '.cart-drawer__dialog[open] .cart-confidence-summary__treatment',
  ].join(', '),
  reevaluationEvents: ['cart:update', 'dialog:open', 'dialog:close'],
};

const CART_TERMS = {
  scopeSelector: CART_CONFIDENCE.scopeSelector,
  checkoutSelector: '#checkout, .rterms-disable',
  checkboxSelector: '.rterms-checkbox:not([id^="rterms-modal"])',
  validationSelector: '.cart-terms-validation',
  validationId: 'cart-terms-validation-message',
};

const EXPERIMENTS = {
  cartNoSurprises: {
    defaultEnabled: false,
    growthbookKey: 'cart-no-surprises',
    eligibilitySelector: CART_CONFIDENCE.eligibilitySelector,
    reevaluationEvents: CART_CONFIDENCE.reevaluationEvents,
    apply(enabled) {
      document.documentElement.classList.toggle(EXPERIMENT_CLASSES.cartNoSurprises, enabled);
      if (!enabled) hideCartTermsValidation();
    },
  },
  decisionConfidenceCard: {
    defaultEnabled: false,
    growthbookKey: 'decision-confidence-card',
    eligibilitySelector: '.decision-confidence-card__treatment',
    apply(enabled) {
      document.documentElement.classList.toggle(EXPERIMENT_CLASSES.decisionConfidenceCard, enabled);
    },
  },
};

const TELEMETRY = {
  experimentImpressionEvent: 'icse:experiment_impression',
};

const state = {};
const localOverrides = new Map();
const pendingExperimentImpressions = new Set();

/**
 * Publish a GrowthBook assignment to Shopify Customer Events.
 * GrowthBook invokes this callback with the evaluated experiment and variation.
 * The session gate prevents duplicate network events for the same experiment.
 *
 * @param {{key: string}} experiment - GrowthBook experiment assignment
 * @param {{key: string|number}} result - Assigned GrowthBook variation
 */
function publishExperimentImpression(experiment, result) {
  const currentExperimentName = experiment?.key;
  const userVariation = result?.key;

  if (currentExperimentName == null || userVariation == null) return;

  const sessionKey = `gb_experiment_${currentExperimentName}_fired`;

  if (sessionStorage.getItem(sessionKey) || pendingExperimentImpressions.has(sessionKey)) return;

  const publish = window.Shopify?.analytics?.publish;
  if (typeof publish !== 'function') return;

  const payload = {
    experiment_name: currentExperimentName,
    experiment_value: userVariation,
  };

  pendingExperimentImpressions.add(sessionKey);

  let publishResult;
  try {
    publishResult = publish.call(
      window.Shopify.analytics,
      TELEMETRY.experimentImpressionEvent,
      payload,
    );
  } catch (error) {
    pendingExperimentImpressions.delete(sessionKey);
    console.error('[ICSEExperiments] Failed to publish experiment impression:', error);
    return Promise.resolve(false);
  }

  return Promise.resolve(publishResult)
    .then((wasDispatched) => {
      if (wasDispatched === false) {
        console.warn('[ICSEExperiments] Shopify did not dispatch experiment impression:', payload);
        return false;
      }

      sessionStorage.setItem(sessionKey, 'true');
      console.log('[ICSEExperiments] Published to Shopify Customer Events:', payload);
      return true;
    })
    .catch((error) => {
      console.error('[ICSEExperiments] Failed to publish experiment impression:', error);
      return false;
    })
    .finally(() => {
      pendingExperimentImpressions.delete(sessionKey);
    });
}

function apply(name) {
  const applicator = EXPERIMENTS[name]?.apply;

  if (typeof applicator === 'function') {
    applicator(Boolean(state[name]));
  }
}

function set(name, enabled) {
  if (!Object.prototype.hasOwnProperty.call(EXPERIMENTS, name)) return;

  state[name] = Boolean(enabled);
  apply(name);
}

function isEnabled(name) {
  return Boolean(state[name]);
}

function isEligible(config) {
  return !config.eligibilitySelector
    || document.querySelector(config.eligibilitySelector) !== null;
}

function override(name, enabled) {
  if (!Object.prototype.hasOwnProperty.call(EXPERIMENTS, name)) {
    console.warn(`[ICSEExperiments] Unknown local override: ${name}`);
    return false;
  }

  localOverrides.set(name, Boolean(enabled));
  set(name, enabled);
  console.log(`[ICSEExperiments] Local override: ${name} = ${Boolean(enabled)}`);
  return true;
}

function applyLocalPreviewOverrides() {
  if (!LOCAL_PREVIEW.hostnames.has(window.location.hostname)) return;

  const overrides = new URLSearchParams(window.location.search).getAll(LOCAL_PREVIEW.overrideParam);
  overrides.forEach((entry) => {
    const [name, value] = entry.split(':');
    if (!name || !['true', 'false'].includes(value)) return;

    override(name, value === 'true');
  });
}

window.ICSEExperiments = {
  set,
  apply,
  isEnabled,
  publishExperimentImpression,
  override,
};

function hideCartTermsValidation(checkbox) {
  const scope = checkbox?.closest(CART_TERMS.scopeSelector)
    || document.querySelector(CART_TERMS.scopeSelector);
  const validation = scope?.querySelector(CART_TERMS.validationSelector);
  const resolvedCheckbox = checkbox || scope?.querySelector(CART_TERMS.checkboxSelector);

  if (validation) validation.hidden = true;
  resolvedCheckbox?.removeAttribute('aria-invalid');
  resolvedCheckbox?.removeAttribute('aria-describedby');
}

function showCartTermsValidation(checkbox) {
  const scope = checkbox.closest(CART_TERMS.scopeSelector);
  const validation = scope?.querySelector(CART_TERMS.validationSelector);

  if (!validation) return;

  const termsContainer = checkbox.closest('.terms-relentless');
  if (termsContainer && validation.previousElementSibling !== termsContainer) {
    termsContainer.insertAdjacentElement('afterend', validation);
  }

  validation.id = validation.id || CART_TERMS.validationId;
  validation.hidden = false;
  checkbox.setAttribute('aria-invalid', 'true');
  checkbox.setAttribute('aria-describedby', validation.id);
  checkbox.focus({ preventScroll: true });
  validation.scrollIntoView({ behavior: 'smooth', block: 'center' });
}

document.addEventListener('click', (event) => {
  if (!document.documentElement.classList.contains(EXPERIMENT_CLASSES.cartNoSurprises)) return;
  if (!(event.target instanceof Element) || !event.target.closest(CART_TERMS.checkoutSelector)) return;

  const scope = event.target.closest(CART_TERMS.scopeSelector);
  if (!scope) return;

  const checkbox = scope.querySelector(CART_TERMS.checkboxSelector);
  if (!checkbox || checkbox.checked) {
    hideCartTermsValidation(checkbox);
    return;
  }

  event.preventDefault();
  event.stopPropagation();
  showCartTermsValidation(checkbox);
}, true);

document.addEventListener('change', (event) => {
  if (!(event.target instanceof HTMLInputElement)) return;
  if (!event.target.matches(CART_TERMS.checkboxSelector) || !event.target.checked) return;
  if (!event.target.closest(CART_TERMS.scopeSelector)) return;

  hideCartTermsValidation(event.target);
}, true);

// Initialize with default states
Object.entries(EXPERIMENTS).forEach(([name, config]) => {
  set(name, config.defaultEnabled);
});
applyLocalPreviewOverrides();

// Integrate GrowthBook SDK queue
window.growthbook_queue = window.growthbook_queue || [];
window.growthbook_queue.push((gb) => {
  gb.setTrackingCallback(publishExperimentImpression);

  const evaluateAll = () => {
    Object.entries(EXPERIMENTS).forEach(([name, config]) => {
      if (!config.growthbookKey) return;

      if (localOverrides.has(name)) {
        window.ICSEExperiments.set(name, localOverrides.get(name));
        return;
      }

      if (!isEligible(config)) {
        window.ICSEExperiments.set(name, config.defaultEnabled);
        return;
      }

      const enabled = gb.isOn(config.growthbookKey);
      window.ICSEExperiments.set(name, enabled);
    });
  };

  // Run initial evaluation
  evaluateAll();

  let evaluationQueued = false;
  const queueEvaluation = () => {
    if (evaluationQueued) return;

    evaluationQueued = true;
    queueMicrotask(() => {
      evaluationQueued = false;
      evaluateAll();
    });
  };

  const reevaluationEvents = new Set(
    Object.values(EXPERIMENTS).flatMap((config) => config.reevaluationEvents || []),
  );
  reevaluationEvents.forEach((eventName) => {
    // Capture non-bubbling dialog events; defer evaluation until synchronous cart morphs finish.
    document.addEventListener(eventName, queueEvaluation, true);
  });

  // Re-run evaluation if GrowthBook receives real-time updates or attribute changes
  gb.setRenderer(() => {
    evaluateAll();
  });
});
